import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Loader2, Plus, Minus, Maximize2 } from "lucide-react";
import { geoNaturalEarth1, geoPath } from "d3-geo";
import { COUNTRY_COORDS } from "../hooks/utils";
import countriesTopo from "./countries110m";

// ── ISO 3166-1 numeric → alpha-2 mapping (for country polygon identification) ──
const NUM_TO_A2 = {
  "004": "AF",
  "008": "AL",
  "012": "DZ",
  "020": "AD",
  "024": "AO",
  "028": "AG",
  "032": "AR",
  "036": "AU",
  "040": "AT",
  "044": "BS",
  "048": "BH",
  "050": "BD",
  "051": "AM",
  "056": "BE",
  "064": "BT",
  "068": "BO",
  "070": "BA",
  "072": "BW",
  "076": "BR",
  "084": "BZ",
  "090": "SB",
  "096": "BN",
  100: "BG",
  104: "MM",
  108: "BI",
  112: "BY",
  116: "KH",
  120: "CM",
  124: "CA",
  140: "CF",
  144: "LK",
  148: "TD",
  152: "CL",
  156: "CN",
  170: "CO",
  178: "CG",
  180: "CD",
  188: "CR",
  191: "HR",
  192: "CU",
  196: "CY",
  203: "CZ",
  204: "BJ",
  208: "DK",
  214: "DO",
  218: "EC",
  222: "SV",
  226: "GQ",
  231: "ET",
  232: "ER",
  233: "EE",
  242: "FJ",
  246: "FI",
  250: "FR",
  262: "DJ",
  266: "GA",
  268: "GE",
  270: "GM",
  275: "PS",
  276: "DE",
  288: "GH",
  300: "GR",
  308: "GD",
  320: "GT",
  324: "GN",
  328: "GY",
  332: "HT",
  340: "HN",
  348: "HU",
  352: "IS",
  356: "IN",
  360: "ID",
  364: "IR",
  368: "IQ",
  372: "IE",
  376: "IL",
  380: "IT",
  384: "CI",
  388: "JM",
  392: "JP",
  398: "KZ",
  400: "JO",
  404: "KE",
  408: "KP",
  410: "KR",
  414: "KW",
  417: "KG",
  418: "LA",
  422: "LB",
  426: "LS",
  428: "LV",
  430: "LR",
  434: "LY",
  440: "LT",
  442: "LU",
  450: "MG",
  454: "MW",
  458: "MY",
  466: "ML",
  478: "MR",
  480: "MU",
  484: "MX",
  496: "MN",
  498: "MD",
  504: "MA",
  508: "MZ",
  512: "OM",
  516: "NA",
  524: "NP",
  528: "NL",
  540: "NC",
  548: "VU",
  554: "NZ",
  558: "NI",
  562: "NE",
  566: "NG",
  578: "NO",
  586: "PK",
  591: "PA",
  598: "PG",
  600: "PY",
  604: "PE",
  608: "PH",
  616: "PL",
  620: "PT",
  624: "GW",
  626: "TL",
  630: "PR",
  634: "QA",
  642: "RO",
  643: "RU",
  646: "RW",
  682: "SA",
  686: "SN",
  694: "SL",
  702: "SG",
  703: "SK",
  704: "VN",
  705: "SI",
  706: "SO",
  710: "ZA",
  716: "ZW",
  724: "ES",
  728: "SS",
  729: "SD",
  732: "EH",
  740: "SR",
  748: "SZ",
  752: "SE",
  756: "CH",
  760: "SY",
  762: "TJ",
  764: "TH",
  768: "TG",
  780: "TT",
  784: "AE",
  788: "TN",
  792: "TR",
  795: "TM",
  800: "UG",
  804: "UA",
  807: "MK",
  818: "EG",
  826: "GB",
  834: "TZ",
  840: "US",
  854: "BF",
  858: "UY",
  860: "UZ",
  862: "VE",
  887: "YE",
  894: "ZM",
  "010": "AQ",
  "-99": "XK",
};

// ── SVG viewport ──
const SVG_W = 960;
const SVG_H = 480;

// ── Zoom limits ──
const MIN_ZOOM = 1;
const MAX_ZOOM = 6;
const ZOOM_STEP = 0.5;

// ── Natural Earth projection fitting our viewBox ──
const projection = geoNaturalEarth1()
  .translate([SVG_W / 2, SVG_H / 2])
  .scale(153);
const pathGenerator = geoPath(projection);

/**
 * Minimal TopoJSON arc decoding — handles only the "land" object (type GeometryCollection
 * or Polygon/MultiPolygon).  This avoids needing topojson-client as a dependency.
 */
function decodeArc(topology, arcIdx) {
  const arc = topology.arcs[arcIdx < 0 ? ~arcIdx : arcIdx];
  const coords = [];
  let x = 0,
    y = 0;
  for (const [dx, dy] of arc) {
    x += dx;
    y += dy;
    const { translate, scale } = topology.transform;
    coords.push([x * scale[0] + translate[0], y * scale[1] + translate[1]]);
  }
  if (arcIdx < 0) coords.reverse();
  return coords;
}

function decodeRing(topology, indices) {
  let coords = [];
  for (const idx of indices) {
    const decoded = decodeArc(topology, idx);
    // skip duplicate junction point
    coords = coords.concat(coords.length ? decoded.slice(1) : decoded);
  }
  return coords;
}

function topoFeatureToGeo(topology, obj) {
  if (obj.type === "GeometryCollection") {
    return obj.geometries.map(g => topoSingleGeo(topology, g));
  }
  return [topoSingleGeo(topology, obj)];
}

function topoSingleGeo(topology, geom) {
  const props = { ...(geom.properties || {}), id: geom.id };
  if (geom.type === "Polygon") {
    return {
      type: "Feature",
      properties: props,
      geometry: {
        type: "Polygon",
        coordinates: geom.arcs.map(ring => decodeRing(topology, ring)),
      },
    };
  }
  if (geom.type === "MultiPolygon") {
    return {
      type: "Feature",
      properties: props,
      geometry: {
        type: "MultiPolygon",
        coordinates: geom.arcs.map(polygon => polygon.map(ring => decodeRing(topology, ring))),
      },
    };
  }
  return {
    type: "Feature",
    properties: props,
    geometry: geom,
  };
}

// ── Decode bundled TopoJSON at module init (no fetch, no CSP issues) ──
const geoFeatures = topoFeatureToGeo(countriesTopo, countriesTopo.objects.countries);

// ── Build a lookup: alpha-2 code → feature index for quick highlight ──
const featureA2Map = {};
geoFeatures.forEach((f, i) => {
  const numId = String(f.properties.id);
  const a2 = NUM_TO_A2[numId];
  if (a2) featureA2Map[a2] = i;
});

/**
 * Clamp a value between min and max.
 */
function clamp(val, min, max) {
  return Math.min(Math.max(val, min), max);
}

/**
 * CountryMap — SVG bubble-map of chargers by country.
 *
 * Uses d3-geo Natural Earth projection with bundled land outlines.
 * Bubble markers are placed at COUNTRY_COORDS centroids.
 *
 * Props:
 *   countryCounts  — { "US": 80, "IN": 30, … }  (alpha-2 keys)
 *   loading        — show spinner overlay
 *   highlightedCode — externally controlled highlight (from table hover)
 *   onHighlight    — callback(code|null) when a bubble is hovered
 */
export default function CountryMap({ countryCounts = {}, loading = false, highlightedCode = null, onHighlight }) {
  // ── Zoom & pan state ──
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);
  const wrapRef = useRef(null);

  // Internal hover (used when no external control)
  const [internalHover, setInternalHover] = useState(null);
  const activeCode = highlightedCode ?? internalHover;

  const handleHighlight = useCallback(
    code => {
      if (onHighlight) onHighlight(code);
      else setInternalHover(code);
    },
    [onHighlight]
  );

  // ── Zoom handlers ──
  const zoomIn = useCallback(() => {
    setZoom(z => clamp(z + ZOOM_STEP, MIN_ZOOM, MAX_ZOOM));
  }, []);

  const zoomOut = useCallback(() => {
    setZoom(z => {
      const next = clamp(z - ZOOM_STEP, MIN_ZOOM, MAX_ZOOM);
      if (next === MIN_ZOOM) setPan({ x: 0, y: 0 });
      return next;
    });
  }, []);

  const resetView = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  // ── Mouse wheel zoom (native listener for { passive: false }) ──
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = e => {
      e.preventDefault();
      setZoom(z => {
        const delta = e.deltaY < 0 ? ZOOM_STEP * 0.5 : -ZOOM_STEP * 0.5;
        const next = clamp(z + delta, MIN_ZOOM, MAX_ZOOM);
        if (next === MIN_ZOOM) setPan({ x: 0, y: 0 });
        return next;
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // ── Drag-to-pan (use refs to avoid stale closure) ──
  const zoomRef = useRef(zoom);
  const panRef = useRef(pan);
  zoomRef.current = zoom;
  panRef.current = pan;

  const handlePointerDown = useCallback(e => {
    // Don't capture if zoom is 1x or if the click is on a zoom button
    if (zoomRef.current <= 1) return;
    if (e.target.closest(".country-map__zoom-controls")) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, startPan: { ...panRef.current } };
  }, []);

  const handlePointerMove = useCallback(e => {
    if (!dragRef.current) return;
    const wrap = wrapRef.current;
    if (!wrap) return;
    const z = zoomRef.current;
    const rect = wrap.getBoundingClientRect();
    const scaleX = SVG_W / rect.width;
    const scaleY = SVG_H / rect.height;
    const dx = (e.clientX - dragRef.current.startX) * scaleX;
    const dy = (e.clientY - dragRef.current.startY) * scaleY;
    const maxPanX = ((z - 1) * SVG_W) / (2 * z);
    const maxPanY = ((z - 1) * SVG_H) / (2 * z);
    setPan({
      x: clamp(dragRef.current.startPan.x + dx / z, -maxPanX, maxPanX),
      y: clamp(dragRef.current.startPan.y + dy / z, -maxPanY, maxPanY),
    });
  }, []);

  const handlePointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  // Build lookup for tooltip positioning (country centroid → SVG coords)
  const countryInfo = useMemo(() => {
    const map = {};
    Object.entries(countryCounts).forEach(([code, count]) => {
      const upper = code.trim().toUpperCase();
      const coords = COUNTRY_COORDS[upper];
      const val = Number(count) || 0;
      if (!coords || val <= 0) return;
      const [x, y] = projection([coords.lng, coords.lat]) || [0, 0];
      map[upper] = { code: upper, name: coords.name, count: val, x, y };
    });
    return map;
  }, [countryCounts]);

  // Compute viewBox based on zoom + pan
  const vbW = SVG_W / zoom;
  const vbH = SVG_H / zoom;
  const vbX = (SVG_W - vbW) / 2 - pan.x;
  const vbY = (SVG_H - vbH) / 2 - pan.y;
  const viewBox = `${vbX} ${vbY} ${vbW} ${vbH}`;

  return (
    <div className="country-map">
      <div
        className={`country-map__canvas-wrap${zoom > 1 ? " country-map__canvas-wrap--zoomed" : ""}`}
        ref={wrapRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      >
        <svg viewBox={viewBox} className="country-map__svg" preserveAspectRatio="xMidYMid meet">
          {/* Background */}
          <rect x={vbX} y={vbY} width={vbW} height={vbH} className="country-map__bg" />

          {/* Country shapes — individual polygons with hover highlight */}
          {geoFeatures.map((feature, i) => {
            const rawId = feature.properties.id;
            const numId = rawId != null ? String(rawId) : null;
            const a2 = numId ? NUM_TO_A2[numId] : null;
            const hasData = a2 && countryCounts[a2] > 0;
            const isActive = a2 && activeCode === a2;
            return (
              <path
                key={numId ?? `geo-${i}`}
                d={pathGenerator(feature) || ""}
                className={
                  "country-map__land" +
                  (hasData ? " country-map__land--has-data" : "") +
                  (isActive ? " country-map__land--active" : "")
                }
                onMouseEnter={hasData ? () => handleHighlight(a2) : undefined}
                onMouseLeave={hasData ? () => handleHighlight(null) : undefined}
                style={hasData ? { cursor: "pointer" } : undefined}
              />
            );
          })}

          {/* Graticule (optional grid) */}
          <path d={pathGenerator({ type: "Sphere" }) || ""} fill="none" className="country-map__sphere" />
        </svg>

        {/* Zoom controls */}
        <div className="country-map__zoom-controls">
          <button
            className="country-map__zoom-btn"
            onClick={zoomIn}
            disabled={zoom >= MAX_ZOOM}
            title="Zoom in"
            aria-label="Zoom in"
          >
            <Plus size={16} />
          </button>
          <button
            className="country-map__zoom-btn"
            onClick={zoomOut}
            disabled={zoom <= MIN_ZOOM}
            title="Zoom out"
            aria-label="Zoom out"
          >
            <Minus size={16} />
          </button>
          <button
            className="country-map__zoom-btn"
            onClick={resetView}
            disabled={zoom === 1}
            title="Reset view"
            aria-label="Reset view"
          >
            <Maximize2 size={14} />
          </button>
        </div>

        {/* Hover tooltip */}
        {activeCode &&
          (() => {
            const info = countryInfo[activeCode];
            if (!info) return null;
            // Translate country centroid SVG coords to % position within the visible viewport
            const pctX = ((info.x - vbX) / vbW) * 100;
            const pctY = ((info.y - vbY) / vbH) * 100;
            return (
              <div
                className="country-map__tooltip"
                style={{
                  left: `${clamp(pctX, 5, 95)}%`,
                  top: `${clamp(pctY, 5, 95)}%`,
                }}
              >
                <strong>{info.name}</strong>
                <span className="country-map__tooltip-count">
                  {info.count.toLocaleString()} charger{info.count !== 1 ? "s" : ""}
                </span>
              </div>
            );
          })()}

        {loading && (
          <div className="geo-map-loading">
            <Loader2 size={16} className="geo-map-loading__spinner" /> Loading…
          </div>
        )}
      </div>
    </div>
  );
}

CountryMap.propTypes = {
  countryCounts: PropTypes.object,
  loading: PropTypes.bool,
  highlightedCode: PropTypes.string,
  onHighlight: PropTypes.func,
};
